import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { t } from '../utils/translation';
import { useForm, Controller } from 'react-hook-form';
import { X, Loader2, Check } from 'lucide-react';
import Select from './ui/Select';
import { supplierService } from '../lib/firebaseService';
import '../styles/components-extended.css';

export default function ProductModal({ isOpen, onClose, product, onSave }) {
    const { register, handleSubmit, control, reset, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            category: 'Alcohol',
            unitType: 'Bottle',
            volumeAmount: 70,
            volumeUnit: 'cl',
            active: true,
            supplierId: ''
        }
    });

    const { data: suppliers } = useQuery({
        queryKey: ['suppliers'],
        queryFn: supplierService.getSuppliers,
        enabled: isOpen
    });

    useEffect(() => {
        if (product) {
            reset(product);
        } else {
            reset({
                category: 'Alcohol',
                brand: '',
                name: '',
                cost: '',
                price: '',
                abv: '',
                unitType: 'Bottle',
                volumeAmount: 70,
                volumeUnit: 'cl',
                active: true,
                supplierId: ''
            });
        }
    }, [product, reset, isOpen]);

    if (!isOpen) return null;

    const onSubmit = async (data) => {
        const formattedData = {
            ...data,
            cost: parseFloat(data.cost),
            price: parseFloat(data.price),
            abv: data.abv ? parseFloat(data.abv) : 0,
            volumeAmount: parseFloat(data.volumeAmount || 0)
        };

        // Remove empty supplierId to avoid Firebase errors
        if (!formattedData.supplierId || formattedData.supplierId === '') {
            delete formattedData.supplierId;
        }

        await onSave(formattedData);
    };

    const categories = [
        { value: 'Alcohol', label: 'Alcohol' },
        { value: 'Soft', label: 'Soft Drink' },
        { value: 'Food', label: 'Food / Snack' },
        { value: 'Other', label: 'Other' }
    ];

    const units = [
        { value: 'Bottle', label: 'Bottle' },
        { value: 'Can', label: 'Can' },
        { value: 'Liter', label: 'Liter' },
        { value: 'Keg', label: 'Keg' },
        { value: 'Kg', label: 'Kilogram (kg)' },
        { value: 'Pack', label: 'Pack' },
        { value: 'Portion', label: 'Portion' }
    ];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content max-w-lg w-full" onClick={e => e.stopPropagation()}>
                <div className="modal-header flex items-center justify-between">
                    <div>
                        <h3 className="modal-title text-2xl">{product ? t('PRODUCTS.MODAL.EDIT_TITLE') : t('PRODUCTS.MODAL.ADD_TITLE')}</h3>
                        <p className="text-gray-500 text-sm mt-1">{t('PRODUCTS.MODAL.DESC')}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
                    <div className="modal-body space-y-6">

                        {/* Category & Unit Row */}
                        <div className="grid grid-cols-2 gap-5">
                            <Controller
                                name="category"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label={t('PRODUCTS.MODAL.CATEGORY')}
                                        options={categories}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                            <Controller
                                name="unitType"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label={t('PRODUCTS.MODAL.UNIT')}
                                        options={units}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>

                        {/* Supplier Selection */}
                        <div className="form-group">
                            <Controller
                                name="supplierId"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label={t('PRODUCTS.MODAL.SUPPLIER')}
                                        options={[
                                            { value: '', label: t('COMMON.SEARCH') },
                                            ...(suppliers?.map(s => ({ value: s.id, label: s.name })) || [])
                                        ]}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>



                        {/* Volume Row */}
                        <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                            <h4 className="text-sm font-semibold text-purple-900 mb-3 uppercase tracking-wider">Volume Details</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label className="form-label text-xs">Capacity / Volume</label>
                                    <input
                                        className="input bg-white"
                                        type="number" step="0.1"
                                        placeholder="e.g. 70"
                                        {...register('volumeAmount', { required: true, min: 0 })}
                                    />
                                </div>
                                <Controller
                                    name="volumeUnit"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Unit (ml, cl, l)"
                                            options={[
                                                { value: 'ml', label: 'Milliliters (ml)' },
                                                { value: 'cl', label: 'Centiliters (cl)' },
                                                { value: 'l', label: 'Liters (l)' },
                                                { value: 'kg', label: 'Kilograms (kg)' },
                                                { value: 'unit', label: 'Unit (Piece)' }
                                            ]}
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {/* Brand & Name */}
                        <div className="space-y-4">
                            <div className="form-group">
                                <label className="form-label">{t('PRODUCTS.MODAL.BRAND')}</label>
                                <input
                                    className="input py-3"
                                    placeholder="e.g. Absolut"
                                    {...register('brand', { required: 'Brand is required' })}
                                />
                                {errors.brand && <p className="form-error">{errors.brand.message}</p>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">{t('PRODUCTS.MODAL.NAME')}</label>
                                <input
                                    className="input py-3"
                                    placeholder="e.g. Blue Label"
                                    {...register('name', { required: 'Name is required' })}
                                />
                                {errors.name && <p className="form-error">{errors.name.message}</p>}
                            </div>
                        </div>

                        {/* Pricing Row */}
                        <div>
                            <h4 className="form-label mb-3">
                                {t('PRODUCTS.TABLE.COST')} / {t('PRODUCTS.TABLE.PRICE')}
                            </h4>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="form-group">
                                    <label className="form-label text-xs">{t('PRODUCTS.MODAL.COST_PRICE')}</label>
                                    <input
                                        className="input"
                                        type="number" step="0.01"
                                        placeholder="0.00"
                                        {...register('cost', { required: true, min: 0 })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label text-xs">{t('PRODUCTS.MODAL.SELL_PRICE')}</label>
                                    <input
                                        className="input"
                                        type="number" step="0.01"
                                        placeholder="0.00"
                                        {...register('price', { required: true, min: 0 })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label text-xs">{t('PRODUCTS.MODAL.ABV')}</label>
                                    <input
                                        className="input"
                                        type="number" step="0.1"
                                        placeholder="0.0"
                                        {...register('abv')}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className="modal-footer flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className="btn btn-secondary px-6">{t('COMMON.CANCEL')}</button>
                        <button type="submit" className="btn btn-primary px-8" disabled={isSubmitting}>
                            {isSubmitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Check className="mr-2" size={18} />}
                            {t('COMMON.SAVE')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
